/* eslint-disable max-lines */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { Form } from 'react-final-form';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import { filter, find, includes, isArray, isEmpty, map, omit, reduce } from 'lodash';

import TextField from 'common/forms/TextField';
import SelectField from 'common/forms/SelectField';
import {
  validateCombinedAddressLength,
  validateEmail,
  validatePhoneNumber,
  validateZipCode,
} from 'common/forms/formValidations';
import { formatZipCode } from 'common/utils';
import CheckboxField from 'common/forms/CheckboxField';
import guarantorRelationship from 'common/constants/guarantorRelationship';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import { updateGuarantor, validateAddress } from 'store/thunks/patientThunks';
import RadioGroupField from 'common/forms/RadioGroupField';
import { useState } from 'react';

const EditGuarantorDialog = ({ mpi, guarantorId, open, onClose }) => {
  const dispatch = useDispatch();
  const initialValues = useSelector(({ patient }) => {
    return find(patient[mpi]?.guarantors, { guarantorId });
  });
  const patientName = useSelector(
    ({ patient }) => ({
      firstName: patient[mpi]?.firstName,
      middleName: patient[mpi]?.middleName,
      lastName: patient[mpi]?.lastName,
    }),
    shallowEqual
  );
  const [guarantorDataToSave, setGuarantorDataToSave] = useState({});
  const [validatedAddresses, setValidatedAddresses] = useState([]);
  const [isMultipleAddresses, setIsMultipleAddresses] = useState(false);
  const [loading, setLoading] = useState(false);

  const closeConfirmAddressDialog = () => setIsMultipleAddresses(false);

  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      onClose();
      onClose();
      setGuarantorDataToSave({});
    }
  };

  const handleFinalConfirmAddress = (selectedAddress) => {
    const address = validatedAddresses[selectedAddress.addressChoice];
    const formattedProviderData = {
      ...guarantorDataToSave,
      addressLine1: address.line1,
      addressLine2: address.line2,
      city: address.city,
      state: address.state,
      zip: address.zip,
    };

    setGuarantorDataToSave(formattedProviderData);
    setIsMultipleAddresses(false);

    const phoneNumbers = reduce(
      formattedProviderData,
      (results, value, key) => {
        switch (key) {
          case 'mobilePhone':
            return [...results, { phoneNumber: value, tags: ['MOBILE'] }];
          case 'workPhone':
            return [...results, { phoneNumber: value, tags: ['WORK'] }];
          case 'homePhone':
            return [...results, { phoneNumber: value, tags: ['HOME'] }];
          default:
            return results;
        }
      },
      []
    );

    setLoading(true);

    dispatch(
      updateGuarantor({
        guarantor: {
          mpi,
          ...omit(formattedProviderData, ['mobilePhone', 'workPhone', 'homePhone']),
          ...(formattedProviderData.relationship === 'SELF' && patientName),
          phoneNumbers: filter(phoneNumbers, 'phoneNumber'),
          firstName: formattedProviderData.firstName || '',
          lastName: formattedProviderData.lastName || '',
          middleName: formattedProviderData.middleName || '',
          addressLine1: formattedProviderData.addressLine1 || '',
          addressLine2: formattedProviderData.addressLine2 || '',
          city: formattedProviderData.city || '',
          state: formattedProviderData.state || '',
          zip: formattedProviderData.zip || '',
          textsEnabled: !!formattedProviderData.textsEnabled,
          isEmergencyContact: !!formattedProviderData.isEmergencyContact,
        },
      })
    ).then((success) => {
      if (success) {
        handleCloseModal();
      }

      setLoading(false);
      setGuarantorDataToSave({});
    });
  };

  const handleUpdateGuarantor = (formData) => {
    setLoading(true);
    const { addressLine1, addressLine2, city, state, zip } = formData;

    const address = { line1: addressLine1, line2: addressLine2, city, state, zip };
    setGuarantorDataToSave(formData);

    return dispatch(validateAddress({ input: address }))
      .then((result) => {
        if (isArray(result)) {
          setValidatedAddresses(result);
          setLoading(false);
          setIsMultipleAddresses(true);
        }
      })
      .catch(() => setLoading(false));
  };

  return (
    <>
      {isMultipleAddresses && !isEmpty(validatedAddresses) && (
        <Dialog
          open={isMultipleAddresses}
          maxWidth='sm'
          fullWidth
          aria-labelledby='AddPatient-ConfirmAddress-header'
        >
          <Typography variant='h6' sx={{ px: 3, pt: 2 }}>
            Confirm Address
          </Typography>

          <Form
            onSubmit={handleFinalConfirmAddress}
            render={({ values, handleSubmit }) => (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <Typography sx={{ pb: 2 }}>
                    {validatedAddresses.length > 1
                      ? 'Multiple addresses found. Please choose one from the suggestions below.'
                      : 'Please confirm the address below.'}
                  </Typography>
                  <RadioGroupField
                    name='addressChoice'
                    id='AddPatient-AddressChoice-radioField'
                    label=''
                  >
                    <Stack direction='column' alignItems='flex-start' gap={2}>
                      {map(validatedAddresses, (address, index) => (
                        <FormControlLabel
                          key={`validateAddress-${index}`}
                          value={index}
                          control={<Radio sx={{ mr: 1 }} />}
                          sx={{ px: 1 }}
                          label={
                            <ListItemText
                              secondary={
                                <>
                                  <Typography>{address.line1}</Typography>
                                  {address?.line2 && <Typography>{address.line2}</Typography>}
                                  <Typography>
                                    {address.city} , {address.state}, {address.zip}
                                  </Typography>
                                </>
                              }
                            />
                          }
                        />
                      ))}
                    </Stack>
                  </RadioGroupField>
                </DialogContent>
                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={closeConfirmAddressDialog}>
                    Cancel
                  </Button>
                  <Button
                    variant='contained'
                    disabled={isEmpty(values?.addressChoice)}
                    onClick={handleSubmit}
                    type='submit'
                  >
                    Confirm
                  </Button>
                </DialogActions>
              </form>
            )}
          />
        </Dialog>
      )}

      <Dialog
        open={open}
        onClose={handleCloseModal}
        aria-describedby='PatientView-EditGuarantorDialog-header'
        fullWidth
      >
        <DialogTitle id='PatientView-EditGuarantorDialog-header'>Edit Guarantor</DialogTitle>

        <Form
          initialValues={{
            ...omit(initialValues, ['patientRelationship']),
            relationship: initialValues?.patientRelationship.relationship,
            mobilePhone: find(initialValues?.phoneNumbers, ({ tags }) => includes(tags, 'MOBILE'))
              ?.phoneNumber,
            workPhone: find(initialValues?.phoneNumbers, ({ tags }) => includes(tags, 'WORK'))
              ?.phoneNumber,
            homePhone: find(initialValues?.phoneNumbers, ({ tags }) => includes(tags, 'HOME'))
              ?.phoneNumber,
            ...guarantorDataToSave,
          }}
          onSubmit={handleUpdateGuarantor}
          render={({ handleSubmit, invalid, values }) => (
            <form noValidate onSubmit={handleSubmit}>
              <DialogContent>
                <SelectField
                  name='relationship'
                  id='PatientView-EditGuarantorDialog-relationship-dropdown'
                  label='Relationship'
                  required
                >
                  {map(guarantorRelationship, (value, key) => (
                    <MenuItem key={key} value={key}>
                      {value}
                    </MenuItem>
                  ))}
                </SelectField>

                {values.relationship !== 'SELF' && (
                  <>
                    <TextField
                      id='PatientView-EditGuarantorDialog-firstName-input'
                      label='First Name'
                      name='firstName'
                      required
                    />

                    <TextField
                      id='PatientView-EditGuarantorDialog-middleName-input'
                      label='Middle Name'
                      name='middleName'
                    />

                    <TextField
                      id='PatientView-EditGuarantorDialog-lastName-input'
                      label='Last Name'
                      name='lastName'
                      required
                    />
                  </>
                )}

                <TextField
                  id='PatientView-EditGuarantorDialog-email-input'
                  label='Email'
                  name='email'
                  required
                  validations={[validateEmail()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-mobilePhoneNumber-input'
                  label='Mobile Phone Number'
                  name='mobilePhone'
                  required
                  validations={[validatePhoneNumber()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-workPhoneNumber-input'
                  label='Work Phone Number'
                  name='workPhone'
                  validations={[validatePhoneNumber()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-homePhoneNumber-input'
                  label='Home Phone Number'
                  name='homePhone'
                  validations={[validatePhoneNumber()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-addressLine1-input'
                  label='Address Line 1'
                  name='addressLine1'
                  validations={[validateCombinedAddressLength()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-addressLine2-input'
                  label='Address Line 2'
                  name='addressLine2'
                  validations={[validateCombinedAddressLength()]}
                />

                <TextField
                  id='PatientView-EditGuarantorDialog-city-input'
                  label='City'
                  name='city'
                />

                <SelectField
                  id='PatientView-EditGuarantorDialog-state-dropdown'
                  label='State'
                  name='state'
                >
                  {map(usStateAbbreviation, ({ name, abbreviation }) => (
                    <MenuItem value={abbreviation} key={abbreviation}>
                      {name}
                    </MenuItem>
                  ))}
                </SelectField>

                <TextField
                  id='PatientView-EditGuarantorDialog-zip-input'
                  label='Zip'
                  name='zip'
                  type='zip'
                  maxLength={10}
                  format={formatZipCode}
                  validations={[validateZipCode()]}
                />

                <CheckboxField
                  label='Text Notification Preference'
                  name='textsEnabled'
                  id='PatientView-EditGuarantorDialog-textNotificationPreference-checkbox'
                />

                <CheckboxField
                  label='Emergency Contact'
                  name='isEmergencyContact'
                  id='PatientView-EditGuarantorDialog-isEmergencyContact-checkbox'
                />
              </DialogContent>

              <DialogActions>
                <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                  Cancel
                </Button>
                <LoadingButton
                  loading={loading}
                  variant='contained'
                  disabled={invalid}
                  type='submit'
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </>
  );
};

EditGuarantorDialog.propTypes = {
  mpi: PropTypes.string.isRequired,
  guarantorId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditGuarantorDialog;
